import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchOnboardingStatus } from '../content-onboarding/hooks/useOnboardingState';
import { apiService } from '../../services/api';
import Sidebar from './components-legacy/Sidebar';
import Header from './components-legacy/Header';
import SocialPresenceOverview from './components-legacy/SocialPresenceOverview';
import CommunicationDirection from './components-legacy/CommunicationDirection';
import CompetitorSocialReferences from './components-legacy/CompetitorSocialReferences';
import ContentPlanningEngine from './components-legacy/ContentPlanningEngine';
import ContentCalendarWorkspace from './components-legacy/ContentCalendarWorkspace';
import ContentEditor from './components-legacy/ContentEditor';
import ExportDownloadCenter from './components-legacy/ExportDownloadCenter';
import GovernanceDashboard from './components-legacy/GovernanceDashboard';
import ExecutionGovernanceEngine from './components-legacy/ExecutionGovernanceEngine';
import PerformanceIntelligenceEngine from './components-legacy/PerformanceIntelligenceEngine';
import PerformanceContentIntelligence from './components-legacy/PerformanceContentIntelligence';
import MarketConsumerTGIntelligence from './components-legacy/MarketConsumerTGIntelligence';
import PredictiveStrategyRecalibration from './components-legacy/PredictiveStrategyRecalibration';
import AudienceContentIntelligence from './components-legacy/AudienceContentIntelligence';
import CommentManager from './components-legacy/CommentManager';
import ErrorBoundary from './components-legacy/ErrorBoundary';

// Sections reachable via a ?section= deep link (e.g. from the notification
// bell). Kept explicit so a hand-typed URL can't open a "coming soon" section
// the sidebar keeps locked.
const DEEP_LINK_SECTIONS = new Set(['content-calendar-workspace']);

export default function ContentEngine() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState('communication-direction');
  const [darkMode, setDarkMode] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  // Post to open in the Content Calendar Workspace editor once its posts load.
  const [focusPostId, setFocusPostId] = useState<string | null>(null);

  // Deep link: ?section=<id>&post=<postId>. Consumed once, then stripped from
  // the URL so a reload behaves normally and a repeat click re-triggers it.
  useEffect(() => {
    const section = searchParams.get('section');
    const post = searchParams.get('post');
    if (!section && !post) return;
    if (section && DEEP_LINK_SECTIONS.has(section)) setActiveSection(section);
    if (post) setFocusPostId(post);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const userId = localStorage.getItem('user_id');
    if (userId) {
      fetchOnboardingStatus(userId)
        .then((status) => {
          // No status in the response: show the page, same as a failed check.
          if (status && !status.is_initialized) {
            navigate('/content-onboarding');
          } else {
            setIsChecking(false);
            // Prepare the other pages' reports in the background while this
            // one is read; a failure only means a page builds when opened.
            apiService.warmUpSmmPages(userId).catch(() => undefined);
          }
        })
        .catch((err) => {
          console.error('Failed to fetch onboarding status:', err);
          setIsChecking(false);
        });
    } else {
      setIsChecking(false);
    }
  }, [navigate]);

  const pageInfo = useMemo(() => {
    const infoMap: Record<string, { title: string; subtitle: string }> = {
      'social-presence-overview': { title: 'Social Presence Overview', subtitle: 'Evaluate current social media maturity and performance health across platforms' },
      'communication-direction': { title: 'Communication Direction', subtitle: 'Define how the brand should communicate across platforms' },
      'competitor-social-references': { title: 'Competitor References', subtitle: 'Benchmark brand performance against competitors and extract strategic gaps' },
      'content-planning-engine': { title: 'Content Planning Engine', subtitle: 'Generate monthly content calendar with themes and objectives' },
      'content-calendar-workspace': { title: 'Content Calendar Workspace', subtitle: 'Full monthly execution view with editable post objects' },
      'comment-manager': { title: 'Comments', subtitle: 'Read and manage comments on your Instagram posts' },
      'content-editor': { title: 'Content Editor', subtitle: 'Deep-level editing of individual posts' },
      'export-download-center': { title: 'Export & Download Center', subtitle: 'Export content packages in multiple formats' },
      'governance-dashboard': { title: 'Governance Dashboard', subtitle: 'Single operational visibility layer' },
      'execution-governance-engine': { title: 'Execution Governance Engine', subtitle: 'Track post lifecycle, campaign governance, and pending workload visibility' },
      'performance-intelligence-engine': { title: 'Performance Intelligence Engine', subtitle: 'Deep-dive into content performance metrics, scores, and audience sentiment' },
      'performance-content-intelligence': { title: 'Performance & Content Intelligence Engine', subtitle: 'Content Efficiency Score leaderboard, theme & format matrix, and fatigue anomaly detection' },
      'market-consumer-tg-intelligence': { title: 'Market, Consumer & TG Intelligence Engine', subtitle: 'External trend feeds, consumer attention behavior shifts, and static target group mapping' },
      'predictive-strategy-recalibration': { title: 'Predictive Strategy Recalibration & Platform Evolution', subtitle: 'Decision intelligence forecasts, AI recommendation feed, and algorithmic tracking' },
      'audience-content-intelligence': { title: 'Audience Intelligence', subtitle: 'Map audience behavior and sentiment to content type and theme performance per channel' },
    };
    return infoMap[activeSection] || { title: 'Content Engine', subtitle: 'Plan and manage your channel specific content renditions' };
  }, [activeSection]);

  if (isChecking) {
    return (
      <div className={`min-h-screen ${darkMode ? 'dark bg-gray-900' : 'bg-gray-50'} flex items-center justify-center transition-colors duration-300`}>
        <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const renderContent = () => {
    switch (activeSection) {
      case 'social-presence-overview':
        return <SocialPresenceOverview />;
      case 'communication-direction':
        return <CommunicationDirection />;
      case 'competitor-social-references':
        return <CompetitorSocialReferences />;
      case 'content-planning-engine':
        return <ContentPlanningEngine setActiveSection={setActiveSection} />;
      case 'comment-manager':
        return <CommentManager />;
      case 'content-calendar-workspace':
        return (
          <ContentCalendarWorkspace
            focusPostId={focusPostId}
            onFocusPostHandled={() => setFocusPostId(null)}
          />
        );
      case 'content-editor':
        return <ContentEditor />;
      case 'export-download-center':
        return <ExportDownloadCenter />;
      case 'governance-dashboard':
        return <GovernanceDashboard />;
      case 'execution-governance-engine':
        return <ExecutionGovernanceEngine />;
      case 'performance-intelligence-engine':
        return <PerformanceIntelligenceEngine />;
      case 'performance-content-intelligence':
        return <PerformanceContentIntelligence />;
      case 'market-consumer-tg-intelligence':
        return <MarketConsumerTGIntelligence />;
      case 'predictive-strategy-recalibration':
        return <PredictiveStrategyRecalibration />;
      case 'audience-content-intelligence':
        return <AudienceContentIntelligence />;
      default:
        return <SocialPresenceOverview />;
    }
  };

  return (
    <ErrorBoundary>
      <div className={`h-screen overflow-hidden ${darkMode ? 'dark bg-gray-900' : 'bg-gray-50'} transition-colors duration-300`}>
        <div className="flex h-full">
          <Sidebar
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            darkMode={darkMode}
          />

          <div className="flex-1 flex flex-col h-full">
            <Header
              darkMode={darkMode}
              setDarkMode={setDarkMode}
              pageTitle={pageInfo.title}
              pageSubtitle={pageInfo.subtitle}
            />

            <main className="flex-1 p-6 overflow-auto">
              {/* Per-section boundary: a crash only takes out this section, and switching sections resets it. */}
              <ErrorBoundary key={activeSection}>
                {renderContent()}
              </ErrorBoundary>
            </main>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
}
